import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Pictogram } from "../components/brand/Pictogram";
import { Logo } from "../components/brand/Logo";
import { CaseCard } from "../components/CaseCard";
import { FaqList } from "../components/FaqList";
import { CtaBand } from "../components/CtaBand";
import { themeBySlug, THEMES, CASES, STEPS, IMG, onColor } from "../data/content";

const Hero = ({ t }) => (
  <section data-testid="service-hero" className="relative bg-ink text-white pt-28 lg:pt-36 overflow-hidden grain">
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10 pb-16 lg:pb-20 items-center">
      <Reveal className="lg:col-span-7">
        <Logo theme={t.color} label={t.name} size="lg" />
        <h1 className="mt-10 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02]">{t.tagline}</h1>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to={`/devis?service=${t.slug}`} data-testid="service-hero-quote-btn" className="btn-primary" style={{ background: t.color, color: onColor(t.color) }}>Devis {t.name} <ArrowRight size={18} /></Link>
          <Link to="/support" className="btn-ghost-light" data-testid="service-hero-support-btn">Assistance</Link>
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-5 relative">
        <div className="aspect-square overflow-hidden relative">
          <img src={IMG[t.image]} alt={t.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, #2b2b2b 0%, transparent 60%)` }} />
          <div className="absolute bottom-6 left-6 h-20 w-20 grid place-items-center bg-ink"><Pictogram name={t.icon} color={t.color} size={50} /></div>
        </div>
      </Reveal>
    </div>
    <span className="block h-2 w-full" style={{ background: t.color }} />
  </section>
);

const Pains = ({ t }) => (
  <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
    <Reveal className="lg:col-span-4">
      <Eyebrow color={t.color}>Vous vous reconnaissez ?</Eyebrow>
      <h2 className="mt-5 font-display text-3xl lg:text-4xl font-extrabold">Les problèmes que nous réglons.</h2>
    </Reveal>
    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4" data-testid="service-pains">
      {t.pains.map((p, i) => (
        <Reveal key={p} delay={i * 0.06} className="bg-white border border-ink/10 p-6 flex gap-4">
          <span className="h-8 w-8 shrink-0 grid place-items-center" style={{ background: `${t.color}22`, color: t.color }}><X size={16} /></span>
          <p className="text-ink/80">{p}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const InHouse = ({ t }) => (
  <section className="bg-ink text-white grain relative" data-testid="service-inhouse">
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28">
      <div className="grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <Eyebrow color={t.color} light>{t.inHouse.eyebrow || "Notre propre expérience"}</Eyebrow>
          <h2 className="mt-5 font-display text-3xl lg:text-4xl font-extrabold">{t.inHouse.title}</h2>
          <p className="mt-4 text-white/65">{t.inHouse.lead}</p>
          <ul className="mt-10 space-y-3" data-testid="service-inhouse-results">
            {t.inHouse.results.map((r) => (
              <li key={r} className="flex items-center gap-3 font-display text-lg font-bold"><ArrowRight size={18} style={{ color: t.color }} />{r}</li>
            ))}
          </ul>
        </Reveal>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10">
          {t.inHouse.items.map(([title, d], i) => (
            <Reveal key={title} delay={i * 0.05} className={`bg-ink p-6 ${i === t.inHouse.items.length - 1 && t.inHouse.items.length % 2 ? "sm:col-span-2" : ""}`}>
              <span className="font-display text-sm font-bold" style={{ color: t.color }}>0{i + 1}</span>
              <h3 className="mt-3 font-display text-lg font-bold">{title}</h3>
              <p className="mt-2 text-sm text-white/60">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Offers = ({ t }) => (
  <section className="bg-white border-y border-ink/10">
    <div className="mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28">
      <Eyebrow color={t.color}>Ce qu'ENOVA met en place</Eyebrow>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10" data-testid="service-offers">
        {t.offers.map(([title, desc], i) => (
          <Reveal key={title} delay={i * 0.05} className={`bg-white p-8 group ${i === t.offers.length - 1 ? `${t.offers.length % 2 ? "md:col-span-2" : "md:col-span-1"} ${["lg:col-span-1", "lg:col-span-3", "lg:col-span-2"][t.offers.length % 3]}` : ""}`}>
            <span className="font-display text-sm font-bold" style={{ color: t.color }}>0{i + 1}</span>
            <h3 className="mt-4 font-display text-xl font-bold">{title}</h3>
            <p className="mt-3 text-sm text-ink/65 leading-relaxed">{desc}</p>
            <span className="mt-6 block h-[3px] w-10 group-hover:w-20 transition-[width] duration-500" style={{ background: t.color }} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Steps = ({ t }) => (
  <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28">
    <Eyebrow color={t.color}>Comment on travaille</Eyebrow>
    <ol className="mt-12 grid md:grid-cols-5 gap-6" data-testid="service-steps">
      {STEPS.map(([s, d], i) => (
        <Reveal key={s} delay={i * 0.07}>
          <li className="border-t-2 pt-5" style={{ borderColor: t.color }}>
            <span className="font-display text-4xl font-extrabold text-ink/15">{i + 1}</span>
            <h3 className="font-display text-lg font-bold mt-2">{s}</h3>
            <p className="text-sm text-ink/60 mt-2">{d}</p>
          </li>
        </Reveal>
      ))}
    </ol>
    {t.brands.length > 0 && (
      <div className="mt-16 flex flex-wrap items-center gap-x-10 gap-y-4" data-testid="service-brands">
        <span className="text-xs uppercase tracking-[0.2em] text-ink/50">Matériel & partenaires</span>
        {t.brands.map((b) => <span key={b} className="font-display text-xl font-bold text-ink/40">{b}</span>)}
      </div>
    )}
  </section>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const t = themeBySlug(slug);
  if (!t) return <Navigate to="/services" replace />;
  const related = CASES.filter((c) => c.theme === t.slug);
  const others = THEMES.filter((x) => x.slug !== t.slug);
  return (
    <div data-testid={`service-page-${t.slug}`} style={{ "--theme": t.color }}>
      <Hero t={t} />
      <Pains t={t} />
      {t.inHouse && <InHouse t={t} />}
      <Offers t={t} />
      <Steps t={t} />
      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-20 lg:pb-28">
          <Eyebrow color={t.color}>{related.length > 1 ? "Réalisations" : "Réalisation liée"}</Eyebrow>
          <div className="mt-10 grid md:grid-cols-2 gap-5">{related.map((c) => <CaseCard key={c.id} c={c} />)}</div>
        </section>
      )}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Eyebrow color={t.color}>Questions fréquentes</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-extrabold">{t.name} : vos questions.</h2>
        </div>
        <div className="lg:col-span-8"><FaqList items={t.faq} color={t.color} prefix="service-faq" /></div>
      </section>
      <CtaBand color={t.color} service={t.slug} title={`Un projet ${t.name.toLowerCase()} ? Demandez votre devis.`} />
      <nav className="bg-paper border-t border-ink/10" data-testid="service-other-themes">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-8 flex flex-wrap gap-3">
          {others.map((o) => (
            <Link key={o.slug} to={`/services/${o.slug}`} data-testid={`service-other-${o.slug}`} className="flex items-center gap-2 px-4 py-2 border border-ink/15 hover:bg-white text-sm transition-colors">
              <Pictogram name={o.icon} color={o.color} size={20} strokeWidth={4} /> {o.name}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
